<script lang="ts" setup>
	import { computed, onBeforeUnmount, onMounted, watch } from "vue";
	import { storeToRefs } from "pinia";
	import { ChevronRight, LogOut, Monitor, X } from "lucide-vue-next";
	import { useMobileMenu } from "@/composables/useMobileMenu";
	import { useLayoutPreference } from "@/composables/useLayoutPreference";
	import { useNavItems } from "@/composables/useNavItems";
	import { useUser } from "@/stores/user";
	import { getAccountMenuSections } from "@/utils/accountMenu";

	// The phone stand-in for the desktop sidebar tail, the credits pill and the
	// hover account menu, none of which work on a touch screen. One bottom sheet
	// holds every destination and every balance.
	const menu = useMobileMenu();
	const layout = useLayoutPreference();
	const { items, activeId } = useNavItems();
	const { user } = storeToRefs(useUser());
	const route = useRoute();

	const hasProfilePicture = computed(() => {
		const profilePicture = user.value?.profile_picture;
		if (!profilePicture) return false;
		if (typeof profilePicture === "string" && profilePicture.toLowerCase() === "none") return false;
		return true;
	});

	const fullName = computed(() => `${user.value?.first_name ?? ""} ${user.value?.last_name ?? ""}`.trim());

	const credits = computed(() => {
		const list = [
			...(user.value?.access_companies ? [{ key: "company", label: "Company", value: user.value?.credits }] : []),
			...(user.value?.access_ucc ? [{ key: "ucc", label: "UCC", value: user.value?.credits_ucc }] : []),
			{ key: "email", label: "Email", value: user.value?.credits_email },
			{ key: "email-validation", label: "Validation", value: user.value?.credits_email_validation },
			{ key: "mobile", label: "Mobile", value: user.value?.credits_mobile },
		];
		return list.map((item) => ({ ...item, display: item.value?.toLocaleString?.() ?? "0" }));
	});

	const sections = computed(() => getAccountMenuSections(user.value));

	// Navigating anywhere closes the sheet, including the page the sheet is
	// already on (a tap on the current item still reads as "go there").
	watch(
		() => route.fullPath,
		() => menu.close()
	);

	// Escape closes the sheet from anywhere on the page, not only while focus is
	// inside it.
	function onKeydown(event: KeyboardEvent) {
		if (event.key === "Escape" && menu.open.value) menu.close();
	}

	onMounted(() => window.addEventListener("keydown", onKeydown));
	onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<template>
	<Teleport to="body">
		<Transition name="sheet">
			<div v-if="menu.open.value" class="mobile-menu-root">
				<div class="mobile-menu-backdrop" @click="menu.close()"></div>

				<div id="mobile-menu-sheet" class="mobile-menu" role="dialog" aria-modal="true" aria-label="Menu">
					<div class="sheet-grip" aria-hidden="true"></div>

					<div class="sheet-head">
						<div class="identity">
							<profile-picture v-if="hasProfilePicture" size="medium" :url="user?.profile_picture" :name="fullName || 'User'"></profile-picture>
							<profile-picture v-else size="medium" :name="fullName || 'User'"></profile-picture>
							<div class="identity-text">
								<h3 class="truncate">{{ fullName }}</h3>
								<p class="truncate">{{ user?.client || user?.email }}</p>
							</div>
						</div>
						<button type="button" class="close" aria-label="Close menu" @click="menu.close()">
							<X :size="20" aria-hidden="true" />
						</button>
					</div>

					<div class="sheet-body">
						<section class="credits" aria-label="Credit balances">
							<div v-for="credit in credits" :key="credit.key" class="credit">
								<span class="credit-value">{{ credit.display }}</span>
								<span class="credit-label">{{ credit.label }}</span>
							</div>
						</section>

						<section class="group">
							<p class="group-label">Navigate</p>
							<ul>
								<li v-for="item in items" :key="item.id">
									<NuxtLink :to="item.to" class="row" :class="{ active: item.id === activeId }" :aria-current="item.id === activeId ? 'page' : undefined" @click="menu.close()">
										<span class="row-icon"><component :is="item.icon" :size="20" aria-hidden="true" /></span>
										<span class="row-label">{{ item.label }}</span>
										<span v-if="item.badge" class="row-badge">{{ item.badge }}</span>
										<ChevronRight :size="16" class="row-chevron" aria-hidden="true" />
									</NuxtLink>
								</li>
							</ul>
						</section>

						<!-- Phones only: the desktop layout is one tap away, and the desktop
						     account menu carries the way back. -->
						<section v-if="layout.isMobileDevice.value" class="group">
							<p class="group-label">Display</p>
							<ul>
								<li>
									<button type="button" class="row" @click="layout.useDesktopLayout()">
										<span class="row-icon"><Monitor :size="20" aria-hidden="true" /></span>
										<span class="row-label">Switch to desktop site</span>
										<span class="row-description">Full layout, pinch to zoom. Switch back from the account menu.</span>
										<ChevronRight :size="16" class="row-chevron" aria-hidden="true" />
									</button>
								</li>
							</ul>
						</section>

						<section v-for="section in sections" :key="section.id" class="group">
							<p class="group-label">{{ section.label }}</p>
							<ul>
								<li v-for="item in section.items" :key="item.id">
									<NuxtLink v-if="'to' in item" :to="item.to" class="row" :class="{ active: route.path === item.to }" @click="menu.close()">
										<span class="row-label">{{ item.label }}</span>
										<span v-if="item.description" class="row-description">{{ item.description }}</span>
										<ChevronRight :size="16" class="row-chevron" aria-hidden="true" />
									</NuxtLink>
									<a v-else :href="item.href" class="row" :class="{ danger: item.id === 'logout' }">
										<span v-if="item.id === 'logout'" class="row-icon"><LogOut :size="18" aria-hidden="true" /></span>
										<span class="row-label">{{ item.label }}</span>
										<span v-if="item.description" class="row-description">{{ item.description }}</span>
									</a>
								</li>
							</ul>
						</section>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<style scoped lang="scss">
	.mobile-menu-root {
		position: fixed;
		inset: 0;
		z-index: 1150;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
	}

	.mobile-menu-backdrop {
		position: absolute;
		inset: 0;
		background: rgba(17, 18, 20, 0.4);
	}

	.mobile-menu {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-height: calc(100% - var(--safe-top) - 24px);
		border-radius: var(--radius-xl) var(--radius-xl) 0 0;
		background: var(--bg-surface);
		box-shadow: var(--shadow-lg);
		overflow: hidden;
	}

	.sheet-grip {
		flex-shrink: 0;
		width: 36px;
		height: 4px;
		margin: 8px auto 0;
		border-radius: var(--radius-pill);
		background: var(--border-strong);
	}

	.sheet-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-shrink: 0;
		padding: 12px 16px 12px 20px;
		border-bottom: 1px solid var(--border);

		.identity {
			display: flex;
			align-items: center;
			gap: 12px;
			min-width: 0;
		}

		.identity-text {
			display: flex;
			flex-direction: column;
			gap: 2px;
			min-width: 0;

			h3 {
				margin: 0;
				font-size: 16px;
				line-height: 20px;
			}

			p {
				margin: 0;
				font-size: 13px;
				line-height: 16px;
				color: var(--text-muted);
			}
		}

		.close {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 40px;
			height: 40px;
			border: 0;
			border-radius: 50%;
			background: var(--bg-subtle);
			color: var(--text);
			cursor: pointer;
		}
	}

	.sheet-body {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
		padding: 16px 12px calc(16px + var(--safe-bottom));
	}

	.credits {
		display: grid;
		// As many balances per row as fit at ~100px each, so a six-figure UCC
		// balance is never ellipsized into "102,1…".
		grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
		gap: 6px;
		width: auto;
		margin: 0 4px 18px;

		.credit {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 3px;
			padding: 10px 4px;
			border: 1px solid var(--border);
			border-radius: var(--radius-lg);
			background: var(--bg-subtle);
			min-width: 0;
		}

		.credit-value {
			@include GoogleSans(false);
			font-size: 15px;
			font-weight: 500;
			font-variant-numeric: tabular-nums;
			color: var(--text);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: 100%;
		}

		.credit-label {
			font-size: 10px;
			line-height: 1;
			letter-spacing: 0.04em;
			text-transform: uppercase;
			color: var(--text-muted);
		}
	}

	.group {
		display: flex;
		flex-direction: column;
		width: auto;
		margin-bottom: 14px;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;
			display: flex;
			flex-direction: column;
		}

		li {
			list-style: none;
		}
	}

	.group-label {
		margin: 0 0 4px 12px;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--text-faint);
	}

	// Icon | label (+ description under it) | badge | chevron. A grid rather
	// than a wrapping flex row so the chevron stays on the right when a
	// description is present instead of dropping to its own line.
	// Rows are links or, for actions, buttons dressed the same way.
	button.row {
		width: 100%;
		border: 0;
		background: transparent;
		font: inherit;
		text-align: left;
		cursor: pointer;
	}

	.row {
		display: grid;
		grid-template-columns: auto minmax(0, 1fr) auto auto;
		align-items: center;
		min-height: 48px;
		padding: 8px 12px;
		border-radius: var(--radius-md);
		color: var(--text);
		text-decoration: none;
		-webkit-tap-highlight-color: transparent;

		.row-icon {
			grid-column: 1;
			grid-row: 1 / span 2;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 24px;
			margin-right: 12px;
			color: var(--text-secondary);
		}

		.row-label {
			@include GoogleSans(false);
			grid-column: 2;
			grid-row: 1;
			min-width: 0;
			font-size: 16px;
			font-weight: 500;
			letter-spacing: -0.2px;
			line-height: 22px;
		}

		.row-description {
			grid-column: 2;
			grid-row: 2;
			min-width: 0;
			font-size: 13px;
			line-height: 17px;
			color: var(--text-muted);
		}

		.row-badge {
			grid-column: 3;
			grid-row: 1 / span 2;
			margin-left: 12px;
			min-width: 20px;
			height: 20px;
			padding: 0 6px;
			border-radius: var(--radius-pill);
			background: var(--bg-muted);
			color: var(--text-secondary);
			font-size: 11px;
			font-weight: 600;
			line-height: 20px;
			text-align: center;
		}

		.row-chevron {
			grid-column: 4;
			grid-row: 1 / span 2;
			margin-left: 12px;
			color: var(--text-faint);
		}

		&:active {
			background: var(--bg-subtle);
		}

		&.active {
			background: var(--accent-soft);
			color: var(--accent);

			.row-icon,
			.row-chevron {
				color: var(--accent);
			}
		}

		&.danger {
			color: var(--accent);

			.row-icon {
				color: var(--accent);
			}
		}
	}

	.sheet-enter-active,
	.sheet-leave-active {
		transition: opacity var(--dur-slow) var(--ease-out);

		.mobile-menu {
			transition: transform var(--dur-slow) var(--ease-out);
		}
	}

	.sheet-enter-from,
	.sheet-leave-to {
		opacity: 0;

		.mobile-menu {
			transform: translateY(24px);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.sheet-enter-active,
		.sheet-leave-active,
		.sheet-enter-active .mobile-menu,
		.sheet-leave-active .mobile-menu {
			transition: none;
		}
	}
</style>
